import type { ObjectiveFormData } from './validation';
import type { FieldErrors } from 'react-hook-form';

type FormFieldName = keyof ObjectiveFormData;

/** The form's fields in the order they appear on the page. */
const FIELD_VISUAL_ORDER: FormFieldName[] = [
    'pillarIds',
    'employeeIds',
    'startDate',
    'endDate',
    'title',
    'description',
    'keyResults',
];

/**
 * `data-testid` of the DOM node to focus for each field that actually renders its error inline.
 * A field missing from this map (`description`) has no visible error today, so it
 * falls through to the caller's fallback instead of blocking the submit in silence.
 */
const FIELD_FOCUS_TARGET: Partial<Record<FormFieldName, string>> = {
    pillarIds: 'objective-form-pillar',
    employeeIds: 'objective-employee-search-input',
    startDate: 'objective-form-start-date',
    endDate: 'objective-form-end-date',
    title: 'objective-form-title',
    keyResults: 'objective-form-key-results',
};

const FOCUSABLE_SELECTOR = 'input, select, textarea, button, [role="button"], [tabindex]';

/**
 * Scrolls to and focuses the field behind `testId`, tolerating the handful of shapes the UI
 * Library renders a field as (a bare input, a wrapper around a trigger button, or — real
 * `DateInput` only — a wrapper with no id of its own, whose trigger carries `-date-input`).
 */
const focusFieldByTestId = (root: ParentNode, testId: string): boolean => {
    const container =
        root.querySelector<HTMLElement>(`[data-testid="${testId}"]`) ??
        root.querySelector<HTMLElement>(`[data-testid="${testId}-date-input"]`);

    if (!container) {
        return false;
    }

    const target = container.matches(FOCUSABLE_SELECTOR)
        ? container
        : (container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? container);

    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // `preventScroll` keeps `focus()` from cutting the smooth scroll short with an instant jump.
    target.focus({ preventScroll: true });
    return true;
};

/**
 * Focuses the first invalid field that can show its error inline and reports whether it found
 * one. `false` means the caller must surface the failure another way — react-hook-form's
 * `handleSubmit` silently no-ops on an invalid form, so a blocked submit would otherwise leave
 * the user with no feedback at all.
 *
 * The search starts from `root`, the form itself, never from `document`: on the host the module
 * renders inside a shadow root, which a document-wide query does not enter.
 */
export const focusFirstInvalidField = (
    errors: FieldErrors<ObjectiveFormData>,
    root: ParentNode,
): boolean => {
    const firstErroredField = FIELD_VISUAL_ORDER.find((field) => !!errors[field]);
    const testId = firstErroredField && FIELD_FOCUS_TARGET[firstErroredField];

    return !!testId && focusFieldByTestId(root, testId);
};
